import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, Spin, Typography, Tag, Empty, Button } from 'antd';
import { HistoryOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ChangelogFrame from './ChangelogFrame';

const { Title } = Typography;

// Separate component to prevent re-renders
function ChangelogStepItem({ version, step, lang }) {
    const [loading, setLoading] = useState(true);

    const handleLoad = useCallback(() => {
        setLoading(false);
    }, []);

    const handleSetLoading = useCallback((val) => {
        setLoading(val);
    }, []);

    return (
        <div
            style={{
                border: '1px solid #f0f0f0',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#fafafa'
            }}
        >
            <ChangelogFrame
                version={version}
                file={`${step}.html`}
                onLoad={handleLoad}
                loading={loading}
                setLoading={handleSetLoading}
                lang={lang}
            />
        </div>
    );
}

export default function ChangelogHistory() {
    const { t, i18n } = useTranslation();
    const history = useHistory();
    const [allVersions, setAllVersions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Use system language
    const currentLanguage = useMemo(() => {
        return (i18n.language || 'tr').substring(0, 2);
    }, [i18n.language]);

    // Fetch all versions from index.json
    useEffect(() => {
        const fetchVersions = async () => {
            try {
                const response = await fetch('/changelogs/index.json');
                if (!response.ok) throw new Error('Failed to load changelog index');
                const data = await response.json();

                // Sort versions from newest to oldest
                const sortedVersions = [...data].sort((a, b) => {
                    const vA = a.version.split('.').map(Number);
                    const vB = b.version.split('.').map(Number);
                    for (let i = 0; i < Math.max(vA.length, vB.length); i++) {
                        const numA = vA[i] || 0;
                        const numB = vB[i] || 0;
                        if (numB !== numA) return numB - numA;
                    }
                    return 0;
                });

                setAllVersions(sortedVersions);
                setIsLoading(false);
            } catch (error) {
                console.error('Error loading changelog index:', error);
                setIsLoading(false);
            }
        };

        fetchVersions();
    }, []);

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!allVersions || allVersions.length === 0) {
        return (
            <div style={{ padding: 50 }}>
                <Empty description={t('No changelogs available')} />
            </div>
        );
    }

    return (
        <div style={{
            padding: '24px',
            width: '100%',
            maxWidth: '1400px',
            margin: '0 auto',
            paddingTop: '80px'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '24px',
                gap: '16px'
            }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => history.goBack()}
                    type="text"
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <HistoryOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                    <Title level={3} style={{ margin: 0 }}>{t('Version History')}</Title>
                </div>
            </div>

            {/* Version List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {allVersions.map((versionData, versionIndex) => (
                    <Card
                        key={versionData.version}
                        style={{
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: '12px'
                        }}
                    >
                        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Tag
                                color={versionIndex === 0 ? 'green' : 'blue'}
                                style={{
                                    fontSize: '16px',
                                    padding: '4px 12px',
                                    borderRadius: '6px'
                                }}
                            >
                                v{versionData.version}
                            </Tag>
                            {versionIndex === 0 && (
                                <Tag color="gold">{t('Latest')}</Tag>
                            )}
                        </div>

                        {/* Steps */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {versionData.steps && versionData.steps.map((step) => (
                                <ChangelogStepItem
                                    key={`${versionData.version}-${step}`}
                                    version={versionData.version}
                                    step={step}
                                    lang={currentLanguage}
                                />
                            ))}
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
}
